import React, {FC, ReactNode, useLayoutEffect, useRef, useState} from 'react';
import {getDebug} from '../../../../library/GeneralUse';
import {readPostDocumentsLimit} from '../../../../library/PostsCollection';
import {usePostsStore} from '../../../../stores/PostsStore';

const style = {
	container: `relative   pl-1 recommended:pl-4 pr-4   overflow-hidden`,
	scrollContainer: `flex flex-col gap-y-4   pr-4   h-full   overflow-y-scroll   scrollbar-webkit`,
	gradientOverlay: `absolute left-1 recommended:left-4 right-4 bottom-0   h-24   bg-gradient-to-b from-transparent to-whirlwindDarkest`,
};

type PostsContainerProps = {
	children?: ReactNode;
	dataTestId: string;
	id: string;
	tailwindcss?: string;
};

const DEBUG = getDebug('posts');

export const PostsContainer: FC<PostsContainerProps> = ({
	children,
	dataTestId,
	id,
	tailwindcss,
}) => {
	// Zustand
	const posts = usePostsStore((state) => state.posts);

	// Component variables
	const [showGradient, setShowGradient] = useState(false);
	const postsContainerRef = useRef<HTMLDivElement>(null);

	// Component methods
	const handleScroll = async () => {
		try {
			if (postsContainerRef.current) {
				const scrollHeight = postsContainerRef.current.scrollHeight;
				const scrollTop = postsContainerRef.current.scrollTop;
				const clientHeight = postsContainerRef.current.clientHeight;

				// This is used to determine if we hit the bottom of the postsContainer i.e. the overflow/scroll container for NewsPosts.
				// How it works https://stackoverflow.com/questions/45585542/detecting-when-user-scrolls-to-bottom-of-div-with-react-js
				const bottom =
					Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 1;

				if (bottom) {
					DEBUG && console.log('POSTS BOTTOM REACHED!', bottom);

					const morePostsLoaded = await readPostDocumentsLimit(4);
					DEBUG && console.log(`morePostsLoaded`, morePostsLoaded);
					if (!morePostsLoaded) {
						setShowGradient(false);
					}
				} else {
					setShowGradient(true);
				}
			}
		} catch (error) {
			DEBUG && console.error(error);
		}
	};

	useLayoutEffect(() => {
		if (posts && postsContainerRef.current) {
			const scrollHeight = postsContainerRef.current.scrollHeight;
			const clientHeight = postsContainerRef.current.clientHeight;

			setShowGradient(!(scrollHeight === clientHeight));
		}
	}, [posts]);

	return (
		<div className={`${style.container} ${tailwindcss ? tailwindcss : ''}`}>
			<div
				className={style.scrollContainer}
				data-test-id={dataTestId}
				id={id}
				onScroll={async () => {
					await handleScroll();
				}}
				ref={postsContainerRef}>
				{children}
			</div>
			{showGradient ? <div className={style.gradientOverlay} /> : <></>}
		</div>
	);
};
